import React, { useContext, useState, useCallback, useMemo } from "react";

import { Auth } from "api/makeApiRequest";
import { ApiRequest } from "api/makeApiRequest/ApiRequest";
import ModalConfirm from "components/ModalConfirm";
import { AuthContext } from "components/AuthProvider/AuthProvider";
import { useSendData } from "hooks/useSendData";
import { ucFirst } from "utils/ucFirst";

import { ModalCancelPurchaseContents } from "./ModalCancelPurchase.styles";

export type Props<T> = {
	item: T | undefined;
	isOpen: boolean;
	cancelItem: (
		data: {
			item: T;
			message?: string | undefined;
		},
		auth: Auth
	) => ApiRequest<{}>;
	onCancel: () => Promise<void>;
	onOK: (arg: { hostUid: string }) => Promise<void>;
	itemDescription: string;
	summary: (item: T, currentUid: string) => React.ReactNode;
	heading?: (item: T, currentUid: string) => string;
	extraFormContent?: (item: T, currentUid: string) => React.ReactNode;
};

type CompatibleItem = {
	id: string;
	hostUid: string;
};

function ModalCancelPurchase<T extends CompatibleItem>({
	item,
	isOpen,
	onCancel,
	onOK,
	cancelItem,
	itemDescription,
	summary,
	heading,
	extraFormContent
}: Props<T>) {
	const [{ uid }] = useContext(AuthContext);

	const [message, setMessage] = useState<string | undefined>();

	const resetForm = useCallback(() => {
		setMessage(undefined);
	}, [setMessage]);

	const doCancelItem = useSendData({ method: cancelItem });

	const { hostUid } = item ? item : { hostUid: undefined };

	const handleOK = useCallback(async () => {
		if (!(item && hostUid)) {
			return;
		}

		await doCancelItem(
			{
				item,
				...(message ? { message } : {})
			},
			{ uid }
		).ready;

		await onOK({ hostUid });
		resetForm();
	}, [item, message, hostUid, onOK, uid, resetForm, doCancelItem]);

	const handleCancel = useCallback(async () => {
		await onCancel();
		resetForm();
	}, [onCancel, resetForm]);

	const okText = useMemo(
		() => `${hostUid === uid ? `Cancel` : `Amend`} ${itemDescription}`,
		[hostUid, uid, itemDescription]
	);

	if (!uid) {
		return null;
	}

	if (!item) {
		return null;
	}

	const headingTextFromProps = heading ? heading(item, uid) : undefined;
	const extraFormContentFromProps = extraFormContent
		? extraFormContent(item, uid)
		: undefined;

	return (
		<ModalConfirm
			isOpen={!!item}
			title={`Cancel ${ucFirst(itemDescription)}`}
			onCancel={handleCancel}
			onOK={handleOK}
			okText={okText}
		>
			<ModalCancelPurchaseContents>
				{summary(item, uid)}
				<h3>
					{headingTextFromProps
						? headingTextFromProps
						: `Cancel ${itemDescription}`}
				</h3>

				<form>
					<div className="row gtr-uniform">
						{extraFormContentFromProps ? extraFormContentFromProps : null}
						<div className="col-12">
							<textarea
								placeholder="Accompanying note"
								value={message}
								onChange={e => setMessage(e.currentTarget.value)}
							/>
						</div>
					</div>
				</form>
			</ModalCancelPurchaseContents>
		</ModalConfirm>
	);
}

export default ModalCancelPurchase;
